// React bootstrap table next =>
// DOCS: https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/
// STORYBOOK: https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html
import React, { useEffect, useMemo } from "react";
import { shallowEqual } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider } from "react-bootstrap-table2-paginator";
import * as uiHelpers from "./ProductFilesUIHelper";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import {
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
} from "../../../../../../_metronic/_helpers";
import { useProductFilesUIContext } from "./ProductFilesUIContext";
import {
  ActionsColumnFormatter,
  UploadedDateTimeColumnFormatter,
} from "../../../../../_components/column-formatters";
import { DocumentTypeColumnFormatter } from "../../../../Common/DocumentTypeColumnFormatter";
import * as actions from "../../../_redux/products/productsActions";
import { useIntl } from "react-intl";
import { useEntityDeleteDialogContext } from "../../entity-delete-dialog/EntityDeleteDialogContext";
import { PROJECT_PRODUCT_FILE_TYPES } from "../../../../../_utils/listUtils";
import { useAppSelector } from "../../../../../../redux/hooks";
import { canEdit, isAdmin } from "../../../../../_utils/authUtils";
import { USER } from "../../../../../_utils/userTypes";
import { displayColumnIsConfidential, sortDateFunc } from "../../../../../_utils/columnUtils";
import { useSearch } from "../../../../../hooks/useSearch";
import { columnTags } from "../../../../../_utils/tagsUtils";
import { useFileViewer } from "../../../../../hooks/useFileViewer";

export function ProductFilesTable() {
  const intl = useIntl();
  const { setDeleteEntityDialog } = useEntityDeleteDialogContext();
  const productFilesUIContext = useProductFilesUIContext();
  const { setEntities, entitiesFiltered } = useSearch();
  const { downloadAndOpenFilesInViewer } = useFileViewer();

  const productFilesUIProps = useMemo(() => {
    return {
      ids: productFilesUIContext.ids,
      setIds: productFilesUIContext.setIds,
      filters: productFilesUIContext.filters,
      setFilters: productFilesUIContext.setFilters,
      productId: productFilesUIContext.productId,
      openRequestSignatureDialog: productFilesUIContext.openRequestSignatureDialog,
      openFileDialog: productFilesUIContext.openFileDialog,
      openEditFileDialog: productFilesUIContext.openEditFileDialog,
      readOnly: productFilesUIContext.readOnly,
      context: productFilesUIContext.context,
    };
  }, [productFilesUIContext]);

  const { totalCount, entities, userId, groups, session } = useAppSelector(
    (state) => ({
      totalCount: state.products?.productForEdit?.current?.files?.length,
      entities:
        productFilesUIProps.context === "lead"
          ? state.leads?.leadForEdit?.saved?.product?.files
          : state.products?.productForEdit?.current?.files,
      userId: state.auth.session.id,
      groups: state.auth.groups,
      session: state.auth.session,
    }),
    shallowEqual
  );

  useEffect(() => {
    setEntities(entities);
  }, [entities]);
  const openDeleteFileDialog = (id) => {
    setDeleteEntityDialog({
      action: { fn: actions.deleteFile, props: { id, productId: productFilesUIProps.productId } },
      entityType: "PRODUCT.FILE",
    });
  };

  const columns = [
    {
      dataField: "id",
      text: "",
      sort: false,
      formatter: DocumentTypeColumnFormatter,
    },
    {
      dataField: "friendlyName",
      text: intl.formatMessage({
        id: "COMMON.NAME",
      }),
      sort: true,
      sortCaret: sortCaret,
      headerClasses: "text-left text-nowrap",
      classes: "text-left",
    },
    {
      dataField: "fileType",
      text: intl.formatMessage({
        id: "COMMON.TYPE",
      }),
      sort: true,
      sortCaret: sortCaret,
      headerClasses: "text-left text-nowrap",
      classes: "text-left",
      formatter: (cell) => (
        <span>{intl.formatMessage({ id: PROJECT_PRODUCT_FILE_TYPES[cell] })}</span>
      ),
      sortValue: (cell) => intl.formatMessage({ id: PROJECT_PRODUCT_FILE_TYPES[cell ?? "-"] }),
    },
    {
      dataField: "uploadedAt",
      text: intl.formatMessage({
        id: "COMMON.UPLOADED.DATE",
      }),
      sort: true,
      sortCaret: sortCaret,
      formatter: UploadedDateTimeColumnFormatter,
      formatExtraData: {
        readOnly: productFilesUIProps.readOnly,
        userId,
        intl,
      },
      sortFunc: sortDateFunc,
      headerClasses: "text-left text-nowrap",
      classes: "text-left",
    },
    columnTags(groups, session, intl),
    {
      dataField: "action",
      text: intl.formatMessage({
        id: "ACTION.TITLE",
      }),
      formatter: ActionsColumnFormatter,
      formatExtraData: {
        openRequestSignatureDialog: productFilesUIProps.openRequestSignatureDialog,
        openEditDialog: productFilesUIProps.openEditFileDialog,
        openDeleteDialog: openDeleteFileDialog,
        actionList: ["SIGNATURE", "WRITE", "TRASH"],
        canRequestSignature: () => {
          return isAdmin(groups, session) || session?.userTypes?.includes(USER);
        },
        canDelete: (row) => {
          return canEdit(groups, session, "PRODUCT");
        },
        canEdit: (row) => {
          return canEdit(groups, session, "PRODUCT");
        },
        entityType: "FILE",
      },
      classes: "text-right",
      headerClasses: "text-right",
      style: {
        minWidth: "100px",
      },
      hidden: productFilesUIProps.readOnly,
    },
  ];
  displayColumnIsConfidential({ entitiesFiltered, columns });

  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: productFilesUIProps.filters.pageSize,
    page: productFilesUIProps.filters.pageNumber,
  };

  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination paginationProps={paginationProps}>
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center"
              bordered={false}
              bootstrap4
              keyField="id"
              data={entitiesFiltered || []}
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              hover
              rowEvents={{
                onClick: (e, fileRowItem, rowIndex) => {
                  downloadAndOpenFilesInViewer([fileRowItem]);
                },
              }}
              rowStyle={{ cursor: "pointer" }}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );
      }}
    </PaginationProvider>
  );
}
